"use client";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import Link from "next/link";
import { useTranslations, useLocale } from "next-intl";
import { api, Guide, GuideSection } from "@/lib/api";
import { useRequireAuth } from "@/lib/useRequireAuth";
import { formatDate } from "@/lib/formatDate";

// ── Auto-linking: turn mentions of other pages into links ────────────────────
// F24: sayfa adları dil başına `guide.linkPhrases`'tan gelir (eskiden yalnızca
// en/tr sabitti, dokuz dilin kılavuzu hiç bağlanmıyordu). Her dil İngilizce
// adları da taşır; kılavuz metni bazen onları kullanır.
const LINK_TARGETS: Record<string, string> = {
  dashboard: "/dashboard",
  settings: "/settings",
  createPatient: "/create-patient",
};
type LinkRules = { re: RegExp; hrefOf: Record<string, string> };
const escRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function buildLinkRules(phrasesByTarget: Record<string, unknown>): LinkRules | null {
  const hrefOf: Record<string, string> = {};
  const all: string[] = [];
  for (const [target, href] of Object.entries(LINK_TARGETS)) {
    // Dil dosyasında " | " ile ayrılmış tek satır (i18n denetimi dizi uzunluğu
    // ister, ifade sayısı dilden dile değişir).
    const phrases = phrasesByTarget[target];
    if (typeof phrases !== "string") continue;
    for (const raw of phrases.split("|")) {
      const phrase = raw.trim();
      if (!phrase) continue;
      hrefOf[phrase.toLowerCase()] = href;
      all.push(phrase);
    }
  }
  if (all.length === 0) return null;
  const re = new RegExp("(" + all.sort((a, b) => b.length - a.length).map(escRe).join("|") + ")", "gi");
  return { re, hrefOf };
}

function linkify(text: string, kp: string, rules: LinkRules | null): ReactNode[] {
  if (!rules) return [text];
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  rules.re.lastIndex = 0;
  while ((m = rules.re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const matched = m[0];
    const href = rules.hrefOf[matched.toLowerCase()];
    out.push(
      href ? (
        <Link
          key={`${kp}-${m.index}`}
          href={href}
          className="text-[var(--accent)] underline decoration-[var(--accent)]/35 underline-offset-[3px] hover:decoration-[var(--accent)] transition-colors"
        >
          {matched}
        </Link>
      ) : (
        matched
      )
    );
    last = m.index + matched.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// Bir başlık "… (Pro)" ile bitiyorsa plan adı başlıktan ayrılıp rozet olarak
// yazılır — cümlenin içinde parantez olarak kalması, kılavuzun geri kalanındaki
// düz tipografiyi bozuyordu.
const TIER_BADGE_RE = /\s*[(（]\s*(pro|basic|free)\s*[)）]\s*$/i;
function splitHeading(heading: string): { text: string; badge: string | null } {
  const m = heading.match(TIER_BADGE_RE);
  return m ? { text: heading.replace(TIER_BADGE_RE, ""), badge: m[1].toUpperCase() } : { text: heading, badge: null };
}

export default function GuidePage() {
  const t = useTranslations("guide");
  const linkRules = useMemo(() => buildLinkRules((t.raw("linkPhrases") as Record<string, unknown>) ?? {}), [t]);
  const tNav = useTranslations("sidebar");
  const locale = useLocale();
  const [guide, setGuide] = useState<Guide | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState<number | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const ready = useRequireAuth();
  useEffect(() => {
    if (!ready) return;
    api.getGuide(locale).then(setGuide).catch(() => {}).finally(() => setLoading(false));
  }, [ready, locale]);

  const content = guide?.content;

  const filtered = useMemo(() => {
    if (!content) return [] as { s: GuideSection; i: number }[];
    const all = content.sections.map((s, i) => ({ s, i }));
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter(({ s }) =>
      s.heading.toLowerCase().includes(q) ||
      s.body.toLowerCase().includes(q) ||
      s.bullets.some((b) => b.toLowerCase().includes(q))
    );
  }, [content, query]);

  // Okunan bölüm, soldaki içindekiler listesinde işaretlenir. Ölçü bandı
  // üstteki yapışkan çubuğun altından (96px) görüş alanının %40'ına kadardır;
  // banda giren en üstteki bölüm etkin sayılır. Sayfa dibe dayandıysa son
  // bölüm etkin sayılır: kısa bir son bölümün başlığı banda hiç giremez,
  // çünkü sayfa o kadar aşağı kaymaz, ve liste sondan bir öncekini gösterirdi.
  useEffect(() => {
    const root = bodyRef.current;
    if (!root) return;
    const els = Array.from(root.querySelectorAll<HTMLElement>("[data-sec]"));
    setActive(null);
    if (els.length === 0) return;
    const idxOf = (el: HTMLElement) => Number(el.getAttribute("data-sec"));
    const compute = () => {
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      if (atBottom) { setActive(idxOf(els[els.length - 1])); return; }
      const bandTop = 96;
      const bandBottom = window.innerHeight * 0.4;
      const hit = els
        .map((el) => ({ el, r: el.getBoundingClientRect() }))
        .filter(({ r }) => r.bottom > bandTop && r.top < bandBottom)
        .sort((a, b) => a.r.top - b.r.top)[0];
      if (hit) setActive(idxOf(hit.el));
    };
    let raf = 0;
    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => { raf = 0; compute(); });
    };
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    compute();
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [filtered]);

  const updated = guide?.generated_at ? formatDate(guide.generated_at, locale, "long") : null;

  // Sayfa en üstteyken hiçbir bölüm gözlem penceresine girmez; liste o anda
  // tamamen sönük kalmasın diye ilk bölüm işaretli sayılır.
  const activeIdx = active ?? filtered[0]?.i ?? null;

  const num = (i: number) => String(i + 1).padStart(2, "0");
  const mono = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

  return (
    <>
      {loading || !content ? (
        <div className="flex items-center justify-center py-32">
          <span className="text-[var(--text-muted)] text-sm">{t("loading")}</span>
        </div>
      ) : (
        <div className="max-w-[1100px] mx-auto px-6 md:px-11 pb-24">

          {/* ── Hero ──────────────────────────────────────────────── */}
          <section className="pt-12 md:pt-16 pb-10 border-b border-[var(--border)]">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)]" />
              <span
                className="text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)]"
                style={{ fontFamily: mono }}
              >
                {tNav("guide")}
              </span>
            </div>

            <h1
              className="max-w-[15ch] font-semibold text-[var(--text-primary)]"
              style={{
                fontFamily: "var(--font-lora)",
                fontSize: "clamp(38px, 5.2vw, 68px)",
                lineHeight: 0.98,
                letterSpacing: "-0.035em",
                textWrap: "balance",
              }}
            >
              {content.title}
            </h1>

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] gap-8 lg:gap-14 items-start mt-9">
              {content.intro && (
                <p className="text-[17px] md:text-[18px] leading-[1.62] text-[var(--text-secondary)]" style={{ textWrap: "pretty" }}>
                  {linkify(content.intro, "intro", linkRules)}
                </p>
              )}
              {updated && (
                <div className="flex flex-col gap-3 rounded-[18px] border border-[var(--border)] bg-[var(--surface)] px-5 py-5">
                  <div
                    className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.16em] text-[var(--accent)]"
                    style={{ fontFamily: mono }}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 1 1-3-6.7" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 3v6h-6" />
                    </svg>
                    {t("updated")}
                  </div>
                  <div className="text-[14.5px] leading-[1.55] text-[var(--text-secondary)]">
                    {updated} · {t("autoNote")}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* ── Rail + body ───────────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-[232px_minmax(0,1fr)] gap-10 lg:gap-[72px] items-start pt-10">

            <nav className="lg:sticky lg:top-[88px] flex flex-col gap-1">
              <div className="flex items-center gap-2.5 h-10 px-3.5 mb-3.5 rounded-xl border border-[var(--border)] bg-[var(--surface)]">
                <svg className="w-[15px] h-[15px] shrink-0 text-[var(--text-muted)]" fill="none" stroke="currentColor" strokeWidth={1.9} viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="7" />
                  <path strokeLinecap="round" d="m20 20-3.5-3.5" />
                </svg>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("searchPlaceholder")}
                  className="w-full bg-transparent border-0 outline-none text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
                />
              </div>

              <div
                className="px-3 pt-1.5 pb-2 text-[10.5px] uppercase tracking-[0.18em] text-[var(--text-muted)]"
                style={{ fontFamily: mono }}
              >
                {t("contents")}
              </div>

              {filtered.map(({ s, i }) => {
                const on = activeIdx === i;
                return (
                  <a
                    key={i}
                    href={`#s-${i}`}
                    className={`flex gap-3 px-3 py-2 rounded-[10px] text-sm transition-colors ${
                      on
                        ? "bg-[var(--accent-light)] text-[var(--text-primary)] font-medium"
                        : "text-[var(--text-secondary)] hover:bg-[var(--surface-alt)] hover:text-[var(--text-primary)]"
                    }`}
                  >
                    <span className="text-xs opacity-55 shrink-0" style={{ fontFamily: mono }}>{num(i)}</span>
                    <span className="min-w-0">{splitHeading(s.heading).text}</span>
                  </a>
                );
              })}
            </nav>

            <div ref={bodyRef} className="flex flex-col max-w-[720px]">
              {filtered.length === 0 ? (
                <p className="text-sm text-[var(--text-muted)] border border-dashed border-[var(--border)] rounded-xl p-6 text-center">
                  {t("noResults")}
                </p>
              ) : (
                filtered.map(({ s, i }, pos) => {
                  const { text, badge } = splitHeading(s.heading);
                  const isLast = pos === filtered.length - 1;
                  return (
                    <article
                      key={i}
                      id={`s-${i}`}
                      data-sec={i}
                      className={`scroll-mt-24 pt-7 pb-8 border-t border-[var(--border)] ${isLast ? "border-b" : ""}`}
                    >
                      <div className="flex items-baseline gap-4 mb-3">
                        <span className="text-[13px] tracking-[0.05em] text-[var(--accent)] shrink-0" style={{ fontFamily: mono }}>
                          {num(i)}
                        </span>
                        <h2
                          className="text-[24px] md:text-[27px] font-semibold text-[var(--text-primary)]"
                          style={{ fontFamily: "var(--font-lora)", lineHeight: 1.2, letterSpacing: "-0.025em" }}
                        >
                          {text}
                          {badge && (
                            <span
                              className="inline-block align-[4px] ml-2 px-2.5 py-[3px] rounded-full bg-[var(--accent-light)] text-[var(--accent)] text-[11px] font-medium tracking-[0.08em]"
                              style={{ fontFamily: mono }}
                            >
                              {badge}
                            </span>
                          )}
                        </h2>
                      </div>

                      {s.body && (
                        <p className="text-[16.5px] leading-[1.6] text-[var(--text-secondary)] mb-[18px]" style={{ textWrap: "pretty" }}>
                          {linkify(s.body, `b-${i}`, linkRules)}
                        </p>
                      )}

                      {s.bullets.length > 0 && (
                        <ul className="flex flex-col gap-[11px]">
                          {s.bullets.map((b, j) => (
                            <li key={j} className="flex gap-3 text-[15.5px] leading-[1.55] text-[var(--text-primary)]">
                              <span className="text-[13px] leading-[1.7] text-[var(--accent)] shrink-0" aria-hidden>→</span>
                              <span className="min-w-0">{linkify(b, `l-${i}-${j}`, linkRules)}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </article>
                  );
                })
              )}

              {content.footer && filtered.length > 0 && (
                <p className="mt-9 text-[14.5px] leading-[1.65] text-[var(--text-muted)]" style={{ textWrap: "pretty" }}>
                  {linkify(content.footer, "footer", linkRules)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
